import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Eye, FileText, Trash2 } from 'lucide-react';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';

interface DocumentCardProps {
    document: {
        id: string | number;
        name?: string;
        file_name?: string;
        type?: string;
        document_type?: string;
        status?: string;
        verified?: boolean;
        file_size?: number;
        created_at?: string;
        uploaded_at?: string;
    };
    onView?: (document: any) => void;
    onDownload?: (document: any) => void;
    onDelete?: (documentId: string | number) => void;
    showDelete?: boolean;
    isDeleting?: boolean;
    getDocumentTypeLabel?: (type: string) => string;
    getStatusClassName?: (status: string) => string;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
    document,
    onView,
    onDownload,
    onDelete,
    showDelete = false,
    isDeleting = false,
    getDocumentTypeLabel,
    getStatusClassName,
}) => {
    const defaultGetDocumentTypeLabel = (type: string) => {
        switch (type) {
            case 'id_proof':
                return 'ID Proof';
            case 'address_proof':
                return 'Address Proof';
            case 'income_proof':
                return 'Income Proof';
            case 'passport':
                return 'Passport';
            case 'drivers_license':
                return "Driver's License";
            case 'utility_bill':
                return 'Utility Bill';
            default:
                return type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Document';
        }
    };

    const defaultGetStatusClassName = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'verified':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'rejected':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return '';
        }
    };

    const getDocumentName = () => {
        return document.name || document.file_name || 'Document';
    };

    const getDocumentType = () => {
        return document.type || document.document_type || '';
    };

    const getDocumentDate = () => {
        return document.created_at || document.uploaded_at || '';
    };

    const getDocumentStatus = () => {
        if (document.verified) return 'verified';
        return document.status || '';
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '';
        return `${Math.round(bytes / 1024)} KB`;
    };

    const documentTypeLabel = getDocumentTypeLabel
        ? getDocumentTypeLabel(getDocumentType())
        : defaultGetDocumentTypeLabel(getDocumentType());

    const statusClassName = getStatusClassName
        ? getStatusClassName(getDocumentStatus())
        : defaultGetStatusClassName(getDocumentStatus());

    return (
        <Card className="overflow-hidden">
            <div className="p-4 flex items-start gap-4">
                <div className="bg-muted rounded-md p-2 flex-shrink-0">
                    <FileText className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{getDocumentName()}</h3>
                    <p className="text-sm text-muted-foreground">{documentTypeLabel}</p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                            <DateTimeFormatter date={getDocumentDate()} />
                        </span>
                        {document.file_size && (
                            <span className="text-xs text-muted-foreground">
                                {formatFileSize(document.file_size)}
                            </span>
                        )}
                    </div>
                    <div className="mt-2">
                        <Badge variant="outline" className={statusClassName}>
                            {getDocumentStatus()}
                        </Badge>
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    {onView && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => onView(document)}
                            title="View document"
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                    )}
                    {onDownload && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => onDownload(document)}
                            title="Download document"
                        >
                            <Download className="h-4 w-4" />
                        </Button>
                    )}
                    {showDelete && onDelete && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600"
                            onClick={() => onDelete(document.id)}
                            disabled={isDeleting}
                            title="Delete document"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default DocumentCard;
